import { useRef, useState, useLayoutEffect, type ReactElement } from "react"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"

interface TruncatedTextCellProps {
  /** The full text to display (and to show in the tooltip when truncated). */
  text: string
  /** Fixed content width of the cell, in pixels. Drives both the layout and
   *  the truncation point. */
  widthPx: number
  /** Side on which the tooltip should appear when shown. Defaults to "top". */
  tooltipSide?: "top" | "right" | "bottom" | "left"
}

/**
 * Renders a single line of text inside a fixed-width, truncating span and
 * shows a tooltip with the full text ONLY when the text is actually truncated.
 *
 * Truncation is detected by comparing the element's `scrollWidth` (the full
 * width the content would need) against its `clientWidth` (the width actually
 * available). The span is always kept inside the tooltip trigger so the
 * measured DOM node is stable; only the tooltip *content* is rendered
 * conditionally. This avoids remounting (and re-measuring) the node when the
 * truncation state flips.
 *
 * @param text - The full text to display and to surface in the tooltip.
 * @param widthPx - The fixed content width of the cell in pixels.
 * @param tooltipSide - Optional side for the tooltip (defaults to "top").
 * @returns A tooltip-wrapped span; the tooltip content only appears when the
 *          text is truncated.
 */
const TruncatedTextCell = ({
  text,
  widthPx,
  tooltipSide = "top",
}: TruncatedTextCellProps): ReactElement => {
  const spanRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState<boolean>(false)

  // Measure whether the text overflows its fixed-width container. Runs after
  // layout so the DOM measurements are accurate, and re-measures on resize and
  // once web fonts have loaded.
  useLayoutEffect((): (() => void) | undefined => {
    const element: HTMLSpanElement | null = spanRef.current
    if (element === null) return undefined

    const measure = (): void => {
      // scrollWidth exceeds clientWidth precisely when the content is clipped.
      const nextIsTruncated: boolean = element.scrollWidth > element.clientWidth
      setIsTruncated((prev: boolean): boolean => (prev === nextIsTruncated ? prev : nextIsTruncated))
    }

    measure()

    // Re-measure once web fonts finish loading. The fallback font used during
    // the initial layout can be narrower than the final font, which would
    // otherwise leave a now-truncated value without a tooltip. The span has a
    // fixed width, so the ResizeObserver below will not catch a font swap.
    if (typeof document !== "undefined" && "fonts" in document) {
      void document.fonts.ready.then(measure)
    }

    // Catch any later width changes (e.g. layout shifts around the cell).
    const resizeObserver: ResizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(element)
    return () => resizeObserver.disconnect()
  }, [text, widthPx])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          ref={spanRef}
          className="block truncate cursor-default"
          style={{ width: `${widthPx}px` }}
        >
          {text}
        </span>
      </TooltipTrigger>
      {isTruncated && <TooltipContent side={tooltipSide}>{text}</TooltipContent>}
    </Tooltip>
  )
}

export default TruncatedTextCell
